import { NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

import { PresidioImpersonationCandidate, PresidioImpersonationFacade } from '../services/presidio-impersonation.facade';

/**
 * DEMO MVP 8.2.2 — searchable CSR Co-Browse modal.
 * Search runs only on explicit submit (Search button / Enter); selection requires confirmation.
 */
@Component({
  selector: 'prs-impersonation-select-modal',
  templateUrl: './presidio-impersonation-select-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgFor, FormsModule, TranslatePipe],
})
export class PresidioImpersonationSelectModalComponent implements AfterViewInit, OnDestroy {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly facade = inject(PresidioImpersonationFacade);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  query = '';
  results: PresidioImpersonationCandidate[] = [];
  hasSearched = false;
  searching = false;
  selecting = false;
  pendingUser: PresidioImpersonationCandidate | null = null;
  resultLimit = 50;

  ngAfterViewInit(): void {
    queueMicrotask(() => this.searchInput?.nativeElement?.focus?.());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQueryChange(value: string): void {
    this.query = value;
    this.pendingUser = null;
    // Typing alone does not search — clear prior results until Search is clicked
    if (this.hasSearched || this.results.length) {
      this.hasSearched = false;
      this.results = [];
      this.cdr.markForCheck();
    }
  }

  runSearch(): void {
    if (this.selecting || this.searching) {
      return;
    }
    const trimmed = (this.query || '').trim();
    this.pendingUser = null;
    if (!trimmed) {
      this.hasSearched = false;
      this.results = [];
      this.searching = false;
      this.cdr.markForCheck();
      return;
    }

    this.searching = true;
    this.cdr.markForCheck();
    this.facade
      .searchCandidates(trimmed, this.resultLimit)
      .pipe(
        catchError(() => of([] as PresidioImpersonationCandidate[])),
        finalize(() => {
          this.searching = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(results => {
        this.hasSearched = true;
        this.results = results;
        this.cdr.markForCheck();
      });
  }

  requestConfirm(user: PresidioImpersonationCandidate): void {
    if (this.selecting) {
      return;
    }
    this.pendingUser = user;
    this.cdr.markForCheck();
  }

  cancelConfirm(): void {
    this.pendingUser = null;
    this.cdr.markForCheck();
  }

  async confirmCoBrowse(): Promise<void> {
    const user = this.pendingUser;
    if (!user || this.selecting) {
      return;
    }
    this.selecting = true;
    this.cdr.markForCheck();
    try {
      const ok = await this.facade.start(user.login);
      if (ok) {
        this.activeModal.close(user);
      }
    } finally {
      this.selecting = false;
      this.cdr.markForCheck();
    }
  }

  hide(): void {
    this.activeModal.dismiss();
  }
}
